<template lang='pug'>
div.nopadding
    div.fullwidth.hero-banner
        picture
            source(type='image/webp', srcset='@/assets/application_trafo.webp')
            img.hero-image(src='@/assets/application_trafo.jpg', alt='High Voltage Substation and Transformer Applications', title='Applications - BSS Hochspannungstechnik')

    div.fullwidth(v-if="$route.params.lang == 'en'")
        .content-container
            h1.secondary-header Power Transformers
            h2 Partial Discharge: The Hidden Cause Behind Many Transformer Failures
            
            p Partial Discharge (PD) monitoring is the most effective way to detect early insulation degradation and avoid catastrophic failure. Often, PD is the very first measurable sign that a fault is developing.
            p These small, localized transient arcs repeatedly strike within insulating materials, gradually deteriorating their physical and chemical effectiveness. As the insulation breaks down, the risk of severe dielectric faults increases, ultimately threatening the entire transformer.
            p By accurately measuring and analyzing PD activity, you can stop reacting to unexpected faults. This critical data empowers operators to shift from time-based schedules to a condition-based maintenance program. By utilizing PD trending for predictive maintenance, you can prioritize refurbishments, extend transformer lifespans, and always Stay in Charge of your electrical assets.


            h2 The BSS Difference
            p The BSS PDM-600 is one of the most advanced, yet easy-to-use PD monitoring system available on the market today.


            .card-grid
                MethodBox(
                    title="Multi-Sensor",
                    subtitle="Flexibility",
                    variant="white",
                    linkTo="/en/products/pdm-600",
                    linkText="PDM-600 Monitoring System"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            rect(x="2" y="2" width="20" height="8" rx="2" ry="2")
                            rect(x="2" y="14" width="20" height="8" rx="2" ry="2")
                            circle(cx="6" cy="6" r="1" fill="currentColor")
                            circle(cx="6" cy="18" r="1" fill="currentColor")
                            line(x1="10" y1="6" x2="18" y2="6")
                            line(x1="10" y1="18" x2="18" y2="18")
                    p Maximize your diagnostic capabilities. A single unit can be configured with up to six parallel input channels, allowing you to combine UHF, electrical (IEC 60270), acoustic, and Transient Earth Voltage (TEV) measurements simultaneously.


                MethodBox(
                    title="Noise Immunity",
                    subtitle="UHF Sensing",
                    variant="white",
                    linkTo="/en/products/uhfsensors/overview",
                    linkText="UHF PD Sensors"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            path(d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z")
                            path(d="M8 12h2l1-3 2 6 1-3h2")
                    p Our advanced UHF sensing utilizes the natural Faraday cage effect of the transformer tank, combined with the low-pass filter properties of the capacitive graded bushings. This effectively blocks out electromagnetic interference commonly found in substations and industrial environments, ensuring highly reliable PD detection.

                MethodBox(
                    title="Simple Integration",
                    subtitle="Standard Protocols",
                    variant="white",
                    linkTo="/en/products/pdm-600",
                    linkText="Interfaces & Integration"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            path(d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71")
                            path(d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71")
                    p Integrating into your existing infrastructure is effortless. Our units communicate directly via standard industrial protocols like Modbus TCP and IEC 61850. For proprietary software and custom user interfaces, a built-in REST API and iFrame connectivity offer total flexibility for data visualization. Trusted by industry leaders, this technology is already successfully integrated into systems like MR’s ETOS®.



            h2 System Architecture
            .architecture-wrapper
                img.architecture-image(
                    :src="require('@/assets/svg/Monitoring System Architecture_transparent.svg')",
                    alt="BSS Partial Discharge Monitoring System Architecture",
                    title="BSS Partial Discharge Monitoring System Architecture"
                )
            
            h2 Find the Perfect Fit for Your Application
            
            p While the configurations below represent ideal setups for standard applications, we know that every project is unique. Based on your exact requirements, we can configure customized packages utilizing any combination of sensor types and PDM-600 channels. Every package is delivered ready to install—including the tailored PDM-600, all required sensors, and the necessary connection accessories.

            
            PackagesSection(
                title="PD Monitoring Systems for New Power Transformers",
                description="Window‑type UHF PD sensors are the standard recommended by CIGRE SC A2 for partial discharge monitoring, due to their high sensitivity, flat frequency response, and excellent noise immunity. Because installation requires mounting flanges and dielectric windows to be integrated into the transformer tank during manufacturing, this solution is exclusively suited for new transformer installations.",
                :packages="newTransformerPackagesEn"
            )

            PackagesSection(
                title="Retrofit Solutions for Existing Power Transformers",
                description="These packages are tailored for the modernization and digitalization of your current fleet. The featured sensor types can be easily retrofitted without requiring any structural modifications to the transformer tank. Our recommended configurations provide a seamless path to implementing continuous PD monitoring and integrating condition data into modern asset management systems.",
                :packages="packagesEn"
            )

            .products-section
                h2 Products
                .category
                    .products-grid
                        router-link.product(to='/en/products/pdm-600')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/PDM-600.webp')
                                img(src='@/assets/photos/product_shots/PDM-600.png', width="100%", alt='UHF PD Monitoring System PDM-600', title='UHF PD Monitoring System PDM-600')
                            div.overlay PDM-600
                            div.description Multi-Sensor PD Monitoring System
                        router-link.product(to='/en/products/uhfsensors/uhf-ps1')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/UHF-PS1.webp')
                                img(src='@/assets/photos/product_shots/UHF-PS1.png', width="100%", alt='BSS UHF-PS1 Window-Type UHF Sensor', title='BSS UHF-PS1 Window-Type UHF Sensor')
                            div.overlay UHF-PS1
                            div.description Window-Type UHF PD Sensor
                        router-link.product(to='/en/products/uhfsensors/uhf-dvs-50')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/UHF-DVS-50.webp')
                                img(src='@/assets/photos/for_product_overview/UHF-DVS-50.png', width="100%", alt='BSS UHF-DVS-50 Drain Valve Sensor', title='UHF Drain Valve Sensor UHF-DVS-50')
                            div.overlay UHF-DVS-50
                            div.description Drain Valve UHF PD Sensor for Retrofit at DN50/DN80 Oil Valves

                        router-link.product(v-show="showMoreProducts", to='/en/products/uhfsensors/uhf-dvs-25')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/UHF-DVS-25.webp')
                                img(src='@/assets/photos/for_product_overview/UHF-DVS-25.png', width="100%", alt='BSS UHF-DVS-25 Drain Valve Sensor', title='UHF Drain Valve Sensor UHF-DVS-25')
                            div.overlay UHF-DVS-25
                            div.description Drain Valve UHF PD Sensor for Retrofit at DN25/DN40 Oil Valves
                        router-link.product(v-show="showMoreProducts", to='/en/products/electricalpdsensors/bs-pd')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/BS-PD.webp')
                                img(src='@/assets/photos/for_product_overview/BS-PD.png', width="100%", alt='BSS BS-PD Bushing Sensor', title='PD & Bushing Monitoring Sensor BS-PD')
                            div.overlay BS-PD
                            div.description PD & Bushing Monitoring Sensor for Test Taps
                        router-link.product(v-show="showMoreProducts", to='/en/products/electricalpdsensors/hfct-50')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/HFCT-50.webp')
                                img(src='@/assets/photos/for_product_overview/HFCT-50.png', width="100%", alt='BSS HFCT-50 Split-Core Current Transformer', title='HFCT-50 Split-Core HFCT')
                            div.overlay HFCT-50
                            div.description Split-Core HFCT for Electrical PD Measurement
                        router-link.product(v-show="showMoreProducts", to='/en/products/otherpdsensors/ae-150-m')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/AE-150-M.webp')
                                img(src='@/assets/photos/for_product_overview/AE-150-M.png', width="100%", alt='BSS AE-150-M Acoustic Sensor', title='Acoustic PD Sensor AE-150-M')
                            div.overlay AE-150-M
                            div.description Acoustic PD Sensor
                        router-link.product(v-show="showMoreProducts", to='/en/products/otherpdsensors/tev-cp')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/TEV-CP.webp')
                                img(src='@/assets/photos/product_shots/TEV-CP.png', width="100%", alt='BSS TEV-CP Sensor', title='Transient Earth Voltage PD Sensor TEV-CP')
                            div.overlay TEV-CP
                            div.description Transient Earth Voltage PD Sensor
                    .show-more-btn-wrapper
                        button.show-more-btn(@click="showMoreProducts = !showMoreProducts", :class="{ open: showMoreProducts }")
                            svg(xmlns='http://www.w3.org/2000/svg', width='24', height='24', viewBox='0 0 24 24', fill='none', stroke='currentColor', stroke-width='2.5', stroke-linecap='round', stroke-linejoin='round')
                                polyline(points='6 9 12 15 18 9')

            ContactButton(text="Contact Us")
                span(slot="title") Have questions about our partial discharge solutions?
                span(slot="subtitle") Get in touch with our team for technical consultation, custom configurations, or a tailored quote.

    div.fullwidth(v-if="$route.params.lang == 'de'")
        .content-container
            h1.secondary-header Leistungstransformatoren
            h2 Teilentladung: Die verborgene Ursache vieler Transformatorausfälle

            p Teilentladungs-Monitoring (TE-Monitoring) ist die wirksamste Methode, um einen schleichenden Isolationsabbau frühzeitig zu erkennen und ungeplante Totalausfälle zu vermeiden. Oft sind Teilentladungen das allererste messbare Anzeichen eines drohenden Defekts.
            p Diese kleinen, lokal begrenzten Entladungen treten wiederholt im Isoliermaterial auf und setzen dessen physikalische sowie chemische Widerstandsfähigkeit schrittweise herab. Mit zunehmender Schädigung der Isolation steigt das Risiko eines schwerwiegenden elektrischen Durchschlags, der den gesamten Transformator gefährdet.
            p Durch die präzise Messung und Analyse der TE-Aktivität müssen Sie auf Störungen nicht länger nur reagieren, sondern können proaktiv handeln. Diese Daten ermöglichen den Wechsel von starren, zeitbasierten Wartungsplänen hin zu einer zustandsorientierten Instandhaltungsstrategie. Mithilfe von TE-Trendanalysen können Sie Wartungsarbeiten gezielt priorisieren, die Lebensdauer Ihrer Transformatoren verlängern und behalten stets die Kontrolle über Ihre kritischen Betriebsmittel

            h2 Vorteile des BSS TE-Monitoringsystems

            p Das BSS PDM-600 gehört zu den fortschrittlichsten und zugleich anwenderfreundlichsten TE-Monitoringsystemen auf dem Markt. Es vereint maximale diagnostische Flexibilität mit hoher Empfindlichkeit, sehr guter Störfestigkeit und nahtloser Netzwerk-Integration für moderne Smart-Grid-Anwendungen.

            .card-grid
                MethodBox(
                    title="Multi-Sensor",
                    subtitle="Flexibilität",
                    variant="white",
                    linkTo="/de/products/pdm-600",
                    linkText="PDM-600 Monitoringsystem"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            rect(x="2" y="2" width="20" height="8" rx="2" ry="2")
                            rect(x="2" y="14" width="20" height="8" rx="2" ry="2")
                            circle(cx="6" cy="6" r="1" fill="currentColor")
                            circle(cx="6" cy="18" r="1" fill="currentColor")
                            line(x1="10" y1="6" x2="18" y2="6")
                            line(x1="10" y1="18" x2="18" y2="18")
                    p Maximieren Sie Ihre Diagnosemöglichkeiten: Ein einziges Gerät lässt sich mit bis zu sechs parallelen Eingangskanälen konfigurieren. So können UHF-, elektrische (IEC 60270), akustische und TEV-Messungen (Transient Earth Voltage) nahtlos und zeitgleich miteinander kombiniert werden.

                MethodBox(
                    title="Störfestigkeit",
                    subtitle="UHF-Sensorik",
                    variant="white",
                    linkTo="/de/products/uhfsensors/overview",
                    linkText="UHF-TE-Sensoren"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            path(d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z")
                            path(d="M8 12h2l1-3 2 6 1-3h2")
                    p Unsere fortschrittliche UHF-Sensorik nutzt den Transformatorkessel als natürlichen Faradayschen Käfig kombiniert mit der Tiefpass-Filterwirkung kapazitiv gesteuerter Durchführungen. Dies blockiert effektiv elektromagnetische Störungen, die häufig in Schaltanlagen und industriellen Umgebungen auftreten, und gewährleistet eine zuverlässige TE-Detektion.

                MethodBox(
                    title="Einfache Integration",
                    subtitle="Standardprotokolle",
                    variant="white",
                    linkTo="/de/products/pdm-600",
                    linkText="Schnittstellen & Integration"
                )
                    template(#icon)
                        svg(viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                            path(d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71")
                            path(d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71")
                    p Die Einbindung in Ihre Infrastruktur erfolgt nahtlos: Unsere Systeme kommunizieren direkt über Industriestandards wie Modbus TCP und IEC 61850. Für proprietäre Software und individuelle Dashboards bieten eine integrierte REST-API sowie iFrames maximale Flexibilität bei der Datenvisualisierung. Eine Technologie, auf die Branchenführer vertrauen – bereits erfolgreich integriert in Monitoringsysteme wie ETOS® von Reinhausen.

            h2 Systemarchitektur
            .architecture-wrapper
                img.architecture-image(
                    :src="require('@/assets/svg/Monitoring System Architecture_transparent.svg')",
                    alt="BSS Teilentladungs-Monitoringsystem Architektur",
                    title="BSS Teilentladungs-Monitoringsystem Architektur"
                )

            h2 Finden Sie die perfekte Lösung für Ihre Anwendung

            p Die folgenden Konfigurationen bilden die ideale Grundlage für typische Standardanwendungen. Wir wissen jedoch: Jedes Projekt ist einzigartig. Auf Basis Ihrer spezifischen Anforderungen stellen wir maßgeschneiderte Pakete in beliebiger Kombination von Sensortypen und PDM-600-Kanälen für Sie zusammen. Jedes Paket wird komplett installationsbereit geliefert – inklusive des vorkonfigurierten PDM-600, aller benötigten Sensoren und dem passenden Anschlusszubehör.

            PackagesSection(
                title="TE-Monitoringsysteme für neue Leistungstransformatoren",
                description="UHF-Fenstersensoren sind der von der CIGRE SC A2 empfohlene Standard für das TE-Monitoring. Sie überzeugen durch hohe Empfindlichkeit, einen linearen Frequenzgang und eine hervorragende Störfestigkeit. Da für die Installation Montageflansche und dielektrische Fenster erforderlich sind, die bereits während der Fertigung in den Transformatorkessel integriert werden müssen, ist diese Lösung speziell für Neuanlagen konzipiert.",
                :packages="newTransformerPackagesDe"
            )

            PackagesSection(
                title="Nachrüstlösungen für bestehende Leistungstransformatoren",
                description="Diese Pakete sind speziell auf die Modernisierung und Digitalisierung Ihres bestehenden Transformatorparks zugeschnitten. Die vorgestellten Sensoren lassen sich unkompliziert nachrüsten, ohne dass strukturelle Änderungen am Kessel erforderlich sind. Unsere empfohlenen Konfigurationen bieten Ihnen einen nahtlosen Einstieg in die kontinuierliche TE-Überwachung und integrieren kritische Zustandsdaten direkt in moderne Asset-Management-Systeme.",
                :packages="packagesDe"
            )

            .products-section
                h2 Produkte
                .category
                    .products-grid
                        router-link.product(to='/de/products/pdm-600')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/PDM-600.webp')
                                img(src='@/assets/photos/product_shots/PDM-600.png', width="100%", alt='UHF-TE-Monitoringsystem PDM-600', title='UHF-TE-Monitoringsystem PDM-600')
                            div.overlay PDM-600
                            div.description Multi-Sensor TE-Monitoringsystem
                        router-link.product(to='/de/products/uhfsensors/uhf-ps1')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/UHF-PS1.webp')
                                img(src='@/assets/photos/product_shots/UHF-PS1.png', width="100%", alt='BSS UHF-PS1 Fenstersensor', title='BSS UHF-PS1 Fenstersensor')
                            div.overlay UHF-PS1
                            div.description UHF-TE-Fenstersensor
                        router-link.product(to='/de/products/uhfsensors/uhf-dvs-50')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/UHF-DVS-50.webp')
                                img(src='@/assets/photos/for_product_overview/UHF-DVS-50.png', width="100%", alt='BSS UHF-DVS-50 Ölschiebersensor', title='UHF-Ölschiebersensor UHF-DVS-50')
                            div.overlay UHF-DVS-50
                            div.description UHF-TE-Ölschiebersensor zur Nachrüstung an DN50/DN80-Ölventilen

                        router-link.product(v-show="showMoreProducts", to='/de/products/uhfsensors/uhf-dvs-25')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/UHF-DVS-25.webp')
                                img(src='@/assets/photos/for_product_overview/UHF-DVS-25.png', width="100%", alt='BSS UHF-DVS-25 Ölschiebersensor', title='UHF-Ölschiebersensor UHF-DVS-25')
                            div.overlay UHF-DVS-25
                            div.description UHF-TE-Ölschiebersensor zur Nachrüstung an DN25/DN40-Ölventilen
                        router-link.product(v-show="showMoreProducts", to='/de/products/electricalpdsensors/bs-pd')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/BS-PD.webp')
                                img(src='@/assets/photos/for_product_overview/BS-PD.png', width="100%", alt='BSS BS-PD Durchführungssensor', title='TE- & Durchführungs-Monitoringsensor BS-PD')
                            div.overlay BS-PD
                            div.description TE- & Durchführungs-Monitoringsensor für Messanschlüsse
                        router-link.product(v-show="showMoreProducts", to='/de/products/electricalpdsensors/hfct-50')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/HFCT-50.webp')
                                img(src='@/assets/photos/for_product_overview/HFCT-50.png', width="100%", alt='BSS HFCT-50 Teilkern-Stromwandler', title='HFCT-50 Teilkern-HFCT')
                            div.overlay HFCT-50
                            div.description Teilkern-HFCT für elektrische TE-Messung
                        router-link.product(v-show="showMoreProducts", to='/de/products/otherpdsensors/ae-150-m')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/for_product_overview/AE-150-M.webp')
                                img(src='@/assets/photos/for_product_overview/AE-150-M.png', width="100%", alt='BSS AE-150-M Akustiksensor', title='Akustischer TE-Sensor AE-150-M')
                            div.overlay AE-150-M
                            div.description Akustischer TE-Sensor
                        router-link.product(v-show="showMoreProducts", to='/de/products/otherpdsensors/tev-cp')
                            picture
                                source(type='image/webp', srcset='@/assets/photos/product_shots/TEV-CP.webp')
                                img(src='@/assets/photos/product_shots/TEV-CP.png', width="100%", alt='BSS TEV-CP Sensor', title='Transient Earth Voltage TE-Sensor TEV-CP')
                            div.overlay TEV-CP
                            div.description Transient Earth Voltage TE-Sensor
                    .show-more-btn-wrapper
                        button.show-more-btn(@click="showMoreProducts = !showMoreProducts", :class="{ open: showMoreProducts }")
                            svg(xmlns='http://www.w3.org/2000/svg', width='24', height='24', viewBox='0 0 24 24', fill='none', stroke='currentColor', stroke-width='2.5', stroke-linecap='round', stroke-linejoin='round')
                                polyline(points='6 9 12 15 18 9')

            ContactButton(text="Kontaktieren Sie uns")
                span(slot="title") Haben Sie Fragen zum Teilentladungs-Monitoring?
                span(slot="subtitle") Kontaktieren Sie unser Team für technische Beratung, ein maßgeschneidertes Angebot oder Ihre individuelle Systemkonfiguration.

</template>

<script>
import ContactButton from "@/components/ContactButton.vue";
import PackagesSection from "@/components/PackagesSection.vue";

export default {
    name: "Applications",
    components: {
        ContactButton,
        PackagesSection,
    },
    data() {
        return {
            showMoreProducts: false,
            packagesEn: [
                {
                    title: "Standard Retrofit UHF",
                    badge: "UHF Sensors",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50.svg"),
                    description: "Recommended for retrofitting existing transformers with UHF partial discharge monitoring. The UHF drain‑valve sensors can be installed through standard straight‑opening oil valves without requiring tank modifications. Available for DN 50/80 (UHF‑DVS‑50) and DN 25/40 (UHF‑DVS‑25) valves, this solution is suitable for both new and existing transformers and offers excellent immunity to external interference.",
                    features: [
                        "2x UHF-DVS-50 or 2x UHF-DVS-25 drain valve sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Standard IEC",
                    badge: "IEC Sensors",
                    image: require("@/assets/svg/packages/3x BS-PD.svg"),
                    description: "Provides charge‑based partial discharge monitoring in accordance with IEC 60270. Installed at the bushing test tap, this solution can be combined with continuous bushing monitoring systems measuring capacitance and tan δ. Suitable for both new and existing transformers; installation requires a transformer outage.",
                    features: [
                        "3x BS-PD bushing sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Standard AE",
                    badge: "AE Sensors",
                    image: require("@/assets/svg/packages/4x AE-150-M.svg"),
                    description: "A non‑intrusive solution for partial discharge monitoring using externally mounted acoustic sensors. The sensors are magnetically attached to the transformer tank and detect ultrasonic signals generated by partial discharge activity, allowing installation without any modification of the asset. Suitable for both new and existing transformers.",
                    features: [
                        "4x AE-150-M acoustic sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Plus Retrofit UHF & IEC",
                    badge: "UHF and IEC Sensors",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50 & 3x BS-PD.svg"),
                    description: "Recommended for critical transformers requiring enhanced diagnostic coverage. Combines UHF monitoring via drain‑valve sensors with charge‑based PD measurement according to IEC 60270 via the bushing test taps. Supports integration with continuous bushing monitoring systems measuring capacitance and tan δ. Suitable for both new and existing transformers, without requiring modifications to the transformer tank.",
                    features: [
                        "2x UHF-DVS-50 or 2x UHF-DVS-25 drain valve sensor",
                        "3x BS-PD bushing sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Mixed Retrofit UHF & AE",
                    badge: "UHF and AE Sensors",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50 & 4x AE-150-M.svg"),
                    description: "Combines UHF monitoring via drain‑valve sensors with non‑intrusive acoustic emission monitoring. Magnetically mounted acoustic sensors detect ultrasonic signals generated by partial discharge activity, while the UHF sensors provide excellent noise immunity. Suitable for both new and existing transformers, without requiring modifications to the transformer tank.",
                    features: [
                        "2x UHF-DVS-50 or 2x UHF-DVS-25 drain valve sensor",
                        "4x AE-150-M acoustic sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                }
            ],
            newTransformerPackagesEn: [
                {
                    title: "Basic UHF",
                    badge: "UHF Sensors",
                    image: require("@/assets/svg/packages/2x UHF-PS1.svg"),
                    description: "Recommended for small power transformers with compact tank dimensions and cost‑sensitive projects. Includes all components required for installation, including weld‑on mounting flanges and dielectric windows.",
                    features: [
                        "2x UHF-PS1 window-type sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Standard UHF",
                    badge: "UHF Sensors",
                    image: require("@/assets/svg/packages/4x UHF-PS1.svg"),
                    description: "Recommended as the standard configuration for new transformer installations. The package includes all components required for installation, including the weld‑on mounting flanges and the dielectric windows.",
                    features: [
                        "4x UHF-PS1 window-type sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Standard IEC",
                    badge: "IEC Sensors",
                    image: require("@/assets/svg/packages/3x BS-PD.svg"),
                    description: "Provides charge‑based partial discharge monitoring in accordance with IEC 60270. Installed at the bushing test tap, this solution can be combined with continuous bushing monitoring systems measuring capacitance and tan δ. Suitable for both new and existing transformers; installation requires a transformer outage.",
                    features: [
                        "3x BS-PD bushing sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Plus UHF & IEC",
                    badge: "UHF and IEC Sensors",
                    image: require("@/assets/svg/packages/2x UHF-PS1 & 3x BS-PD.svg"),
                    description: "Combines the excellent noise immunity of UHF measurement with charge‑based PD monitoring according to IEC 60270 via the transformer bushing test taps. Supports integration with continuous bushing monitoring systems measuring capacitance and tan δ. Includes all components required for installation.",
                    features: [
                        "2x UHF-PS1 window-type sensor",
                        "3x BS-PD bushing sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                },
                {
                    title: "Pro UHF",
                    badge: "UHF Sensors",
                    image: require("@/assets/svg/packages/6x UHF-PS1.svg"),
                    description: "Recommended for the most critical assets and particularly suitable for large power transformers with large tank dimensions. The package includes all components required for installation, including the weld‑on mounting flanges and the dielectric windows.",
                    features: [
                        "6x UHF-PS1 window-type sensor",
                        "1x PDM-600 evaluation unit",
                        "Connection accessories",
                    ]
                }
            ],
            packagesDe: [
                {
                    title: "Standard Retrofit UHF",
                    badge: "UHF-Sensoren",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50.svg"),
                    description: "Empfohlen für die Nachrüstung bestehender Transformatoren mit UHF-Teilentladungs-Monitoring. Die UHF-Ölschiebersensoren können über standardmäßige, gerade öffnende Ölventile installiert werden, ohne dass Änderungen am Kessel erforderlich sind. Verfügbar für DN 50/80 (UHF‑DVS‑50) und DN 25/40 (UHF‑DVS‑25) Ventile, eignet sich diese Lösung sowohl für neue als auch für bestehende Transformatoren und bietet hervorragende Störfestigkeit gegenüber externen Einflüssen.",
                    features: [
                        "2x UHF-DVS-50 oder 2x UHF-DVS-25 Ölschiebersensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Standard IEC",
                    badge: "IEC-Sensoren",
                    image: require("@/assets/svg/packages/3x BS-PD.svg"),
                    description: "Bietet ladungsbasiertes Teilentladungs-Monitoring gemäß IEC 60270. Installiert am Durchführungs-Messanschluss, kann diese Lösung mit kontinuierlichen Durchführungs-Monitoringsystemen zur Messung von Kapazität und tan δ kombiniert werden. Geeignet für neue und bestehende Transformatoren; die Installation erfordert eine Transformator-Abschaltung.",
                    features: [
                        "3x BS-PD Durchführungssensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Standard AE",
                    badge: "AE-Sensoren",
                    image: require("@/assets/svg/packages/4x AE-150-M.svg"),
                    description: "Eine nicht-invasive Lösung für Teilentladungs-Monitoring mit extern montierten Akustiksensoren. Die Sensoren werden magnetisch am Transformatorkessel befestigt und detektieren Ultraschallsignale, die durch Teilentladungsaktivität erzeugt werden, sodass die Installation ohne jegliche Modifikation des Betriebsmittels möglich ist. Geeignet für neue und bestehende Transformatoren.",
                    features: [
                        "4x AE-150-M Akustiksensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Plus Retrofit UHF & IEC",
                    badge: "UHF- und IEC-Sensoren",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50 & 3x BS-PD.svg"),
                    description: "Empfohlen für kritische Transformatoren, die eine erweiterte Diagnoseabdeckung erfordern. Kombiniert UHF-Monitoring über Ölschiebersensoren mit ladungsbasierter TE-Messung gemäß IEC 60270 über die Durchführungs-Messanschlüsse. Unterstützt die Integration mit kontinuierlichen Durchführungs-Monitoringsystemen zur Messung von Kapazität und tan δ. Geeignet für neue und bestehende Transformatoren, ohne Modifikationen am Transformatorkessel.",
                    features: [
                        "2x UHF-DVS-50 oder 2x UHF-DVS-25 Ölschiebersensor",
                        "3x BS-PD Durchführungssensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Mixed Retrofit UHF & AE",
                    badge: "UHF- und AE-Sensoren",
                    image: require("@/assets/svg/packages/2x UHF-DVS-50 & 4x AE-150-M.svg"),
                    description: "Kombiniert UHF-Monitoring über Ölschiebersensoren mit nicht-invasiven akustischen Sensoren. Die magnetisch montierten Akustiksensoren detektieren Ultraschallsignale, die durch Teilentladungsaktivität erzeugt werden, während die UHF-Sensoren hervorragende Störfestigkeit bieten. Geeignet für neue und bestehende Transformatoren, ohne Modifikationen am Transformatorkessel.",
                    features: [
                        "2x UHF-DVS-50 oder 2x UHF-DVS-25 Ölschiebersensor",
                        "4x AE-150-M Akustiksensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                }
            ],
            newTransformerPackagesDe: [
                {
                    title: "Basic UHF",
                    badge: "UHF-Sensoren",
                    image: require("@/assets/svg/packages/2x UHF-PS1.svg"),
                    description: "Empfohlen für kleine Leistungstransformatoren mit kompakten Kesselabmessungen und kostensensible Projekte. Enthält alle für die Installation erforderlichen Komponenten, einschließlich Aufschweißflansche und dielektrische Fenster.",
                    features: [
                        "2x UHF-PS1 Fenstersensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Standard UHF",
                    badge: "UHF-Sensoren",
                    image: require("@/assets/svg/packages/4x UHF-PS1.svg"),
                    description: "Empfohlen als Standardkonfiguration für neue Transformatorinstallationen. Das Paket enthält alle für die Installation erforderlichen Komponenten, einschließlich der Aufschweißflansche und der dielektrischen Fenster.",
                    features: [
                        "4x UHF-PS1 Fenstersensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Standard IEC",
                    badge: "IEC-Sensoren",
                    image: require("@/assets/svg/packages/3x BS-PD.svg"),
                    description: "Bietet ladungsbasiertes Teilentladungs-Monitoring gemäß IEC 60270. Installiert am Durchführungs-Messanschluss, kann diese Lösung mit kontinuierlichen Durchführungs-Monitoringsystemen zur Messung von Kapazität und tan δ kombiniert werden. Geeignet für neue und bestehende Transformatoren; die Installation erfordert eine Transformator-Abschaltung.",
                    features: [
                        "3x BS-PD Durchführungssensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Plus UHF & IEC",
                    badge: "UHF- und IEC-Sensoren",
                    image: require("@/assets/svg/packages/2x UHF-PS1 & 3x BS-PD.svg"),
                    description: "Kombiniert die hervorragende Störfestigkeit der UHF-Messung mit ladungsbasiertem TE-Monitoring gemäß IEC 60270 über die Durchführungs-Messanschlüsse des Transformators. Unterstützt die Integration mit kontinuierlichen Durchführungs-Monitoringsystemen zur Messung von Kapazität und tan δ. Enthält alle für die Installation erforderlichen Komponenten.",
                    features: [
                        "2x UHF-PS1 Fenstersensor",
                        "3x BS-PD Durchführungssensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                },
                {
                    title: "Pro UHF",
                    badge: "UHF-Sensoren",
                    image: require("@/assets/svg/packages/6x UHF-PS1.svg"),
                    description: "Empfohlen für die kritischsten Betriebsmittel und besonders geeignet für große Leistungstransformatoren mit großen Kesselabmessungen. Das Paket enthält alle für die Installation erforderlichen Komponenten, einschließlich der Aufschweißflansche und der dielektrischen Fenster.",
                    features: [
                        "6x UHF-PS1 Fenstersensor",
                        "1x PDM-600 Auswerteeinheit",
                        "Anschlusszubehör",
                    ]
                }
            ]
        };
    },
    meta: {
        de: {
            title: "BSS - Anwendungsbereiche",
            description:
                "Anwendungsbereiche und Einsatzgebiete für TE-Monitoring-Systeme und UHF-Sensoren der BSS Hochspannungstechnik GmbH für Leistungstransformatoren, GIS, Kabel und Industrienetze.",
        },
        en: {
            title: "BSS - Applications",
            description:
                "Application areas and use cases for partial discharge monitoring systems and UHF sensors by BSS Hochspannungstechnik GmbH for power transformers, GIS, cables, and industrial grids.",
        },
    },
};
</script>

<style lang='sass' scoped>
.hero-banner
    width: 100%
    overflow: hidden
    line-height: 0
    background-color: #f5f5f5

.hero-image
    width: 100%
    max-height: 420px
    object-fit: cover
    display: block

.content-container
    margin: 40px 40px 60px 40px
    width: calc(100% - 80px)

    @media (max-width: $mobile-breakpoint)
        margin: 25px 20px 40px 20px
        width: calc(100% - 40px)

.architecture-wrapper
    width: 100%
    display: flex
    justify-content: center
    align-items: center
    margin-top: 40px
    margin-bottom: 40px

.architecture-image
    width: 100%
    max-width: 900px
    height: auto
    display: block
    object-fit: contain

.products-section
    margin-top: 50px
    margin-bottom: 20px

    h2
        margin-bottom: 10px

.category
    margin-bottom: 20px
    @media(max-width: $mobile-breakpoint)
        margin-bottom: 0
        width: 100%

    .category-title
        font-weight: 400
        font-size: 1.2em
        margin-bottom: 10px
        margin-top: 20px

    .products-grid
        display: grid
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr))
        gap: 20px
        margin-bottom: 20px
        @media(max-width: $mobile-breakpoint)
            grid-template-columns: repeat(2, 1fr)

        .product
            position: relative
            width: 100%
            background-color: whitesmoke
            border-radius: 4px
            text-decoration: none
            color: inherit
            display: block
            &:hover
                cursor: pointer
                background-color: lightgray
            &:hover .overlay
                display: block
                text-decoration: underline
            &:hover .description
                background-color: lightgray
            img, picture
                padding: 5px
                width: 100%
                height: auto
                aspect-ratio: 1 / 1
                object-fit: contain
                border-radius: 4px
                box-sizing: border-box
            .overlay
                background-color: $color3
                color: white
                padding: 3px 8px
            .description
                background-color: whitesmoke
                border-radius: 0px 0px 4px 4px
                font-size: 0.8em
                word-wrap: break-word
                overflow-wrap: break-word
                white-space: normal
                padding: 8px

.show-more-btn-wrapper
    display: flex
    justify-content: center
    margin-top: 16px

.show-more-btn
    display: flex
    align-items: center
    justify-content: center
    width: 48px
    height: 48px
    background-color: $color2
    color: white
    border: none
    border-radius: 50%
    cursor: pointer
    transition: transform 0.3s ease, opacity 0.2s
    svg
        transition: transform 0.3s ease
    &.open svg
        transform: rotate(180deg)
    &:hover
        opacity: 0.85
</style>